import React, { createContext, useState, useContext, useEffect } from 'react';
import usersData from '../data/users.json';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is stored in localStorage
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Optional: Validate token with backend here
        setUser(parsedUser);
      } catch (err) {
        console.error('Failed to parse stored user:', err);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    setError(null);
    
    // Validate inputs
    if (!email || !password) {
      setError('மின்னஞ்சல் மற்றும் கடவுச்சொல் இரண்டும் தேவை');
      return { success: false, error: 'மின்னஞ்சல் மற்றும் கடவுச்சொல் இரண்டும் தேவை' };
    }
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Find user by email and password
    const foundUser = usersData.find(
      user => user.email === email && user.password === password
    );
    
    if (foundUser) {
      // Generate a token (in real app, this comes from backend)
      // You can either use a predefined token from JSON or generate one
      const authToken = foundUser.token || generateToken(foundUser);
      
      const userData = {
        id: foundUser.id,
        email: foundUser.email,
        name: foundUser.name || foundUser.email,
        role: foundUser.role || 'user'
      };
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', authToken);
      return { success: true };
    } else {
      setError('தவறான மின்னஞ்சல் அல்லது கடவுச்சொல்');
      return { success: false, error: 'தவறான மின்னஞ்சல் அல்லது கடவுச்சொல்' };
    }
  };

  // Helper function to generate a token (for demo purposes)
  const generateToken = (user) => {
    // In production, this should be done by your backend
    return btoa(`${user.id}:${user.email}:${Date.now()}`);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  // Optional: Function to validate token with backend
  // const validateToken = async (token) => {
  //   try {
  //     // In a real app, you would validate the token with your backend
  //     // For demo purposes, we'll just check if token exists in users data
  //     const userWithToken = usersData.find(user => user.token === token);
  //     return !!userWithToken;
  //   } catch (err) {
  //     console.error('Token validation failed:', err);
  //     return false;
  //   }
  // };

  const value = {
    user,
    login,
    logout,
    loading,
    error,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};